import React from "react";
import { FormValues } from "components/medications/create-regional-block/duck";
import { casesActions } from "configs";
import {
  generateId,
  UpdateCardProps,
  useCurrentCase,
  useDispatch,
  useParams,
  convertDoseToMg,
  useInitializeOfflineData
} from "duck";
import {
  RegionalBlockTypeEnum,
  useCreateRegionalBlockMutation,
  useDeleteRegionalBlocksMutation,
  useUpdateRegionalBlockMutation
} from "requests";

export const useDeleteRegionalBlocks = () => {
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const [deleteRegionalBlocks] = useDeleteRegionalBlocksMutation({
    ignoreResults: true
  });
  const shouldSync = useCurrentCase(medicalCase => medicalCase.shouldSync);

  return (ids: (number | string)[]) => {
    if (shouldSync) {
      const filteredIds = ids.filter(id => !Number.isNaN(id));
      return deleteRegionalBlocks({
        variables: {
          input: {
            ids: filteredIds as number[]
          }
        }
      }).then(() => {
        dispatch(casesActions.deleteRegionalBlocks({ data: ids, _id: caseId }));
      });
    }

    dispatch(casesActions.deleteRegionalBlocks({ data: ids, _id: caseId }));
  };
};

export const useCreateRegionalBlock = () => {
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const [create] = useCreateRegionalBlockMutation();
  const { shouldSync, patientId } = useCurrentCase(medicalCase => ({
    shouldSync: medicalCase.shouldSync,
    patientId: medicalCase.data.client.patient._id
  }));
  const { loadMedications } = useInitializeOfflineData();

  return (values: FormValues, type: RegionalBlockTypeEnum) => {
    const variables = {
      type,
      medication: values.name,
      doseMax: convertDoseToMg(values.doseMax, values.doseUnit),
      doseMin: convertDoseToMg(values.doseMin, values.doseUnit),
      concentration: values.concentration,
      volumeToBeInfused: values.volumeToBeInfused,
      initials: values.initials
    };

    if (shouldSync) {
      return create({
        variables: { input: { patient: patientId, ...variables } }
      }).then(response => {
        loadMedications();
        const newRegionalBlock =
          response.data?.createRegionalBlock?.regionalBlock;
        if (newRegionalBlock) {
          dispatch(
            casesActions.addRegionalBlock({
              _id: caseId,
              data: newRegionalBlock
            })
          );
        }
      });
    }

    dispatch(
      casesActions.addRegionalBlock({
        _id: caseId,
        data: { fakeId: generateId(), ...variables }
      })
    );

    return Promise.resolve();
  };
};

export const useUpdateRegionalBlock = () => {
  const [update] = useUpdateRegionalBlockMutation({
    ignoreResults: true
  });
  const dispatch = useDispatch();
  const { shouldSync, caseId } = useCurrentCase(medicalCase => ({
    shouldSync: medicalCase.shouldSync,
    caseId: medicalCase.data._id
  }));

  return React.useCallback(
    (props: UpdateCardProps) => {
      const { idOrFakeId, ...data } = props;

      if (shouldSync) {
        dispatch(
          casesActions.updateRegionalBlock({
            _id: caseId,
            data: {
              ...(typeof idOrFakeId === "number"
                ? { _id: idOrFakeId }
                : { fakeId: idOrFakeId }),
              ...data
            }
          })
        );

        if (typeof idOrFakeId === "number") {
          return update({ variables: { input: { _id: idOrFakeId, ...data } } });
        }
      }

      dispatch(
        casesActions.updateRegionalBlock({
          _id: caseId,
          data: {
            ...(typeof idOrFakeId === "number"
              ? { _id: idOrFakeId }
              : { fakeId: idOrFakeId }),
            ...data
          }
        })
      );
    },
    [update, shouldSync]
  );
};
